/* eslint-disable @typescript-eslint/ban-types */ // Ignore warning about using generic Function type
export type MockableFunction<FuncType> = FuncType & {
  mock: (fn: FuncType) => void,
  unmock: () => void,
};

let allMockedFunctions: Array<MockableFunction<Function>> = [];
let mockingEnabled = false;

/**
 * Must be called before `.mock()` and `.unmock()` methods can be called.
 */
export function enableFunctionMocking(): void {
  mockingEnabled = true;
}

function requireEnabled(): void {
  if (!mockingEnabled) {
    throw new Error('Must call enableFunctionMocking() before .mock()/.unmock()');
  }
}

/**
 * In general, this should be avoided in favor of doing explicit dependency injection
 * (e.g. via function arguments or React context). (It was added primarily for tests that used to
 * use babel-rewire.)
 *
 * This is a simple utility for creating mockable functions for tests.
 *
 * This could be implemented using a full mocking library like sinon, but using something minimal
 * like this avoids including a relatively large library like sinon into the app bundle.
 *
 * It wraps the given function with a wrapper function that has `.mock()` and `.unmock()` methods.
 * `.mock()` takes a single argument, which is the function that should be used to replace the
 * behavior of the original function. `.unmock()` takes no arguments are reverts to the original
 * behavior.
 */
export function createMockableFunction<FuncType extends Function>(fn: FuncType): MockableFunction<FuncType> {
  let mockFn: FuncType | undefined;

  // Note: This is of type `any` instead of `MockableFunction<FuncType>` so we can attach the
  // `.mock()`/`.unmock()` methods after the fact without TypeScript complaining that properties are
  // missing. The return value of the function will still be properly typed, even though this
  // variable is `any`.
  const mockableFn: any = (...args: unknown[]): unknown => (mockFn || fn)(...args);

  mockableFn.mock = (newMockFn: FuncType) => {
    requireEnabled();

    mockFn = newMockFn;
    // Note: We need to keep a list of functions that have been mocked in order to implement
    // `unmockAllFunctions()`, but any function in the list will not be able to be garbage collected
    // until it gets unmocked. It is important that we only add a `mockableFn` to the list after it
    // has actually been mocked, instead of keeping a list of all mockable functions that exist, to
    // avoid unnecessarily holding references to the `mockableFn` that prevent them from being
    // garbage collected.
    allMockedFunctions.push(mockableFn);
  };
  mockableFn.unmock = () => {
    requireEnabled();

    mockFn = undefined;
    allMockedFunctions = allMockedFunctions.filter(otherFn => otherFn !== mockableFn);
  };

  return mockableFn;
}

/**
 * Like calling `.unmock()` on all functions that have been `.mock()`ed. Can be called in a default
 * `afterEach()` hook, for example, so that it is run after all tests by default so you don't have
 * to remember to unmock things manually.
 */
export function unmockAllFunctions(): void {
  requireEnabled();

  const currentMockedFunctions = [...allMockedFunctions];
  allMockedFunctions = [];
  currentMockedFunctions.forEach(fn => fn.unmock());
}
